import { useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import { Aperture, Handshake, Bot, Clock, Drama, Flame, Gavel, Gamepad2, Hand, Hourglass, Mic, Mountain, Orbit, PartyPopper, Pizza, Radar, Siren, Sparkles, Swords, Users, type LucideIcon, VenetianMask, Fingerprint, Spline, MessageSquareQuote, Clapperboard, Star, Briefcase, Film, CaseUpper, ChevronDown, Smartphone, Footprints } from 'lucide-react';
import type { GameId } from '@gmp/shared-types/browser';
import type { GameMeta } from '@gmp/game-sdk';
import { gameInfo } from '../app/catalog';
import { ART } from '../app/art';
import type { PlannedGame } from '../app/upcoming';

export const ICON: Record<string, LucideIcon> = {
  'panic-button': Siren, snapscramble: Aperture, 'act-of-nonsense': Drama, 'wave-command': Radar, 'mercenary-market': Gavel, 'orb-havoc': Orbit, 'battle-bots': Bot, 'meteor-mayhem': Sparkles, 'lava-rising': Flame,
  // One game per line (keeps merges of new games conflict-free).
  objection: Gavel,
  filibuster: Mic,
  dealbreakers: Handshake,
  'double-agent': VenetianMask,
  'the-alibi': Fingerprint,
  'conspiracy-club': Spline,
  'terrible-advice': MessageSquareQuote,
  'plot-twist': Clapperboard,
  'rotten-reviews': Star,
  'human-resources': Briefcase,
  'boulder-bash': Mountain,
  'pizza-heist': Pizza,
  'recess-rumble': Footprints,
  misquoted: Film,
  'initial-reaction': CaseUpper,
  'star-struck': Star,
  'grudge-gauntlet': Swords,
  phonenanigans: Smartphone,
  gavel: Gavel, orbit: Orbit, bot: Bot, meteor: Sparkles, flame: Flame, mountain: Mountain, pizza: Pizza,
};
const CONTROLS: Record<GameMeta['controls'], { label: string; icon: LucideIcon }> = {
  tap: { label: 'Tap controls', icon: Hand },
  'touch-action': { label: 'Action controls', icon: Gamepad2 },
  camera: { label: 'Camera', icon: Aperture },
};

interface CardData {
  id: string;
  name: string;
  tagline: string;
  genre: string;
  players: [number, number];
  minutes: [number, number];
  controls: GameMeta['controls'];
  theme: GameMeta['theme'];
  icon: LucideIcon;
  art?: { icon?: string; cover: string };
  badge: { text: string; tone: 'beta' | 'planned' | 'off' } | null;
  async?: GameMeta['asyncInfo'];
}

function fromMeta(id: GameId, enabled: boolean): CardData {
  const g = gameInfo(id);
  return {
    id, name: g.name, tagline: g.tagline, genre: g.genre, players: [g.minPlayers, g.maxPlayers], minutes: g.estimatedMinutes,
    controls: g.controls, theme: g.theme, icon: ICON[id] ?? Sparkles, art: ART[id],
    badge: !enabled ? { text: 'Not on this server yet', tone: 'off' } : g.readiness === 'beta' ? { text: 'Beta', tone: 'beta' } : null,
    async: g.asyncInfo,
  };
}

const secs = (s: number) => (s < 60 ? `${s}s` : `${Math.round(s / 60)} min`);

function fromPlanned(p: PlannedGame): CardData {
  return {
    id: p.id, name: p.name, tagline: p.tagline, genre: p.genre, players: p.players, minutes: p.minutes, controls: p.controls,
    theme: p.theme, icon: ICON[p.icon] ?? Sparkles, badge: { text: 'Planned', tone: 'planned' },
  };
}

/**
 * `lobby`: the lobby's header card. The badge and info chips move to a full-width row under the icon (so the
 * title and tagline get the width), `actions` sit over the top-right corner and `extra` ends the chip row.
 */
function Card({ c, selected, onClick, compact, lobby, actions, extra }: { c: CardData; selected?: boolean; onClick?: () => void; compact?: boolean; lobby?: boolean; actions?: ReactNode; extra?: ReactNode }) {
  const Icon = c.icon;
  const Tag = onClick ? motion.button : motion.div;
  const inactive = c.badge?.tone === 'planned' || c.badge?.tone === 'off';
  return (
    <Tag
      whileTap={onClick ? { scale: 0.97 } : undefined}
      onClick={onClick}
      aria-pressed={onClick ? selected : undefined}
      aria-disabled={inactive || undefined}
      data-game={c.id}
      data-testid={`game-card-${c.id}`}
      className={`relative overflow-hidden text-left rounded-3xl p-4 border-2 transition ${selected ? 'border-[var(--accent)] shadow-[0_0_0_4px_color-mix(in_oklab,var(--accent)_35%,transparent)]' : 'border-line'} ${inactive ? 'opacity-70' : ''}`}
      style={{ background: `linear-gradient(135deg, ${c.theme.background}, color-mix(in oklab, ${c.theme.primary} 28%, ${c.theme.background}))` }}
    >
      {c.art && (
        <img
          src={c.art.cover}
          alt=""
          aria-hidden
          loading="lazy"
          className="absolute inset-y-0 right-0 h-full w-2/3 object-cover opacity-35 pointer-events-none"
          style={{ maskImage: 'linear-gradient(to right, transparent 15%, black 90%)', WebkitMaskImage: 'linear-gradient(to right, transparent 15%, black 90%)' }}
        />
      )}
      <div className="relative flex items-start gap-3">
        {c.art?.icon ? (
          // Icons are cut-outs with their own frame shape: shown whole (no clipping), with a soft drop shadow.
          <img src={c.art.icon} alt="" aria-hidden width={compact ? 48 : 64} height={compact ? 48 : 64} className={`shrink-0 object-contain drop-shadow-[0_4px_6px_rgba(0,0,0,0.45)] ${compact ? 'size-12' : 'size-16'}`} />
        ) : (
          <div className="rounded-2xl p-2.5 shrink-0" style={{ background: c.theme.primary, color: c.theme.background }}>
            <Icon size={compact ? 24 : 32} aria-hidden />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className={`font-display font-black leading-tight ${lobby ? 'text-[clamp(1.25rem,6.2vw,1.5rem)]' : 'text-2xl'} ${actions ? 'pr-10' : ''}`} style={{ color: c.theme.ink }}>{c.name}</h3>
            {c.badge && !lobby && (
              <span
                className="chip text-[11px] shrink-0"
                // Inline so the .chip background can't override it: the Beta label must stay readable.
                style={c.badge.tone === 'beta' ? { background: 'var(--color-brand-2)', color: '#17121f', borderColor: 'transparent' } : undefined}
                data-testid={`readiness-${c.id}`}
              >
                {c.badge.text}
              </span>
            )}
          </div>
          <p className="text-xs uppercase tracking-wider font-bold mt-0.5" style={{ color: c.theme.secondary }}>{c.genre}</p>
          {!compact && <p className="text-sm text-paper/80 mt-1">{c.tagline}</p>}
          {!lobby && <Chips c={c} />}
          {c.async && !compact && (
            <div className="flex gap-1.5 mt-2 flex-wrap text-[11px]" data-testid={`async-info-${c.id}`}>
              <span className="chip text-[11px]">{c.async.difficulty === 'easy' ? 'Easy to learn' : c.async.difficulty === 'medium' ? 'Some strategy' : 'Deep strategy'}</span>
              <span className="chip text-[11px]">Quick {c.async.match.quick} · Daily {c.async.match.daily}</span>
              {c.async.tags.map((t) => <span key={t} className="chip text-[11px] capitalize">{t}</span>)}
            </div>
          )}
        </div>
      </div>
      {lobby && <Chips c={c} badge extra={extra} />}
      {actions && <div className="absolute top-2 right-2 flex gap-1.5">{actions}</div>}
      {!c.art && <div aria-hidden className="absolute -right-6 -bottom-8 size-28 rounded-full opacity-20" style={{ background: c.theme.secondary }} />}
    </Tag>
  );
}

function Chips({ c, badge, extra }: { c: CardData; badge?: boolean; extra?: ReactNode }) {
  const Ctl = CONTROLS[c.controls];
  return (
    <div className={`relative flex gap-2 flex-wrap items-center ${badge ? 'mt-3' : 'mt-2'}`}>
      {badge && c.badge && (
        <span
          className="chip text-[11px]"
          style={c.badge.tone === 'beta' ? { background: 'var(--color-brand-2)', color: '#17121f', borderColor: 'transparent' } : undefined}
          data-testid={`readiness-${c.id}`}
        >
          {c.badge.text}
        </span>
      )}
      <span className="chip text-[11px]"><Users size={12} /> {c.players[0] === c.players[1] ? c.players[0] : `${c.players[0]}–${c.players[1]}`}</span>
      <span className="chip text-[11px]"><Clock size={12} /> {c.minutes[0]}–{c.minutes[1]} min</span>
      {c.async ? (
        <span className="chip text-[11px]"><Hourglass size={12} /> {secs(c.async.turnSeconds[0])}–{secs(c.async.turnSeconds[1])} turns</span>
      ) : c.controls !== 'tap' && (
        // No "Tap to play" chip: on a card you can tap it reads like an instruction to start.
        <span className="chip text-[11px]"><Ctl.icon size={12} /> {Ctl.label}</span>
      )}
      {extra}
    </div>
  );
}

/** Card for a built game (playable or beta). */
export function GameCard({ id, selected, onClick, compact, enabled = true, lobby, actions, extra }: { id: GameId; selected?: boolean; onClick?: () => void; compact?: boolean; enabled?: boolean; lobby?: boolean; actions?: ReactNode; extra?: ReactNode }) {
  return <Card c={fromMeta(id, enabled)} selected={selected} onClick={enabled ? onClick : undefined} compact={compact} lobby={lobby} actions={actions} extra={extra} />;
}

/** Card for a designed-but-unbuilt game. Never clickable. */
export function PlannedCard({ game, compact }: { game: PlannedGame; compact?: boolean }) {
  return <Card c={fromPlanned(game)} compact={compact} />;
}

/** The 3D arena games (built on @gmp/arena3d): listed as Action games. Every other game is a Social game. */
export const ACTION_GAMES: ReadonlySet<string> = new Set(['battle-bots', 'boulder-bash', 'lava-rising', 'meteor-mayhem', 'orb-havoc', 'pizza-heist', 'recess-rumble', 'wave-command']);
export const gameGroup = (id: string): 'action' | 'social' => (ACTION_GAMES.has(id) ? 'action' : 'social');

const SECTIONS: { id: 'action' | 'social'; title: string; sub: string; icon: LucideIcon }[] = [
  { id: 'action', title: 'Action games', sub: '3D arenas: run, dodge, tag and battle with on-screen controls', icon: Gamepad2 },
  { id: 'social', title: 'Social games', sub: 'Talk, vote, bluff and laugh: live with the whole crowd, or at your own pace', icon: PartyPopper },
];

/**
 * The game list split into category sections. Each section is a collapsed disclosure (a button with
 * aria-expanded, the game count and a chevron) that opens to show all its games; the section holding
 * `selected` starts open. Collapsed cards stay mounted (hidden) so lookups by test id still find them.
 * `card` renders one game.
 */
export function GameSections({ ids, card, compact, enabled, selected }: { ids: readonly GameId[]; card: (id: GameId) => React.ReactNode; compact?: boolean; enabled?: readonly GameId[]; selected?: GameId }) {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set(selected && ids.includes(selected) ? [gameGroup(selected)] : []));
  const toggle = (id: string) => setOpen((o) => {
    const n = new Set(o);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  return (
    <>
      {SECTIONS.map((s) => {
        // Async games still in preview are off on this server: hidden, rather than shown as unavailable.
        // Live games first, then the async ones. Async games still in preview are off on this server: hidden,
        // rather than shown as unavailable.
        const isAsync = (id: GameId) => gameInfo(id).category === 'async';
        const list = ids
          .filter((id) => gameGroup(id) === s.id && (!isAsync(id) || !enabled || enabled.includes(id)))
          .sort((a, b) => Number(isAsync(a)) - Number(isAsync(b)));
        if (!list.length) return null;
        const SIcon = s.icon;
        const isOpen = open.has(s.id);
        const panel = `game-section-panel-${s.id}`;
        return (
          <section key={s.id} className="flex flex-col gap-3" aria-label={s.title} data-testid={`section-${s.id}`} data-open={isOpen ? '1' : '0'}>
            <h2 className="m-0">
              <button
                type="button"
                onClick={() => toggle(s.id)}
                aria-expanded={isOpen}
                aria-controls={panel}
                data-testid={`game-section-${s.id}`}
                className="w-full flex items-center gap-3 mt-2 text-left rounded-3xl border-2 border-line bg-ink-2/80 p-3 transition active:scale-[0.99] focus-visible:outline-3 focus-visible:outline-paper"
              >
                <span className="grid place-items-center rounded-2xl size-10 shrink-0 bg-[var(--accent)] text-ink" aria-hidden><SIcon size={22} /></span>
                <span className="min-w-0 flex-1">
                  <span className={`block font-display font-black leading-tight ${compact ? 'text-lg' : 'text-xl'}`}>
                    {s.title} <span className="chip text-[11px] align-middle ml-1" data-testid={`game-section-count-${s.id}`}>{list.length} {list.length === 1 ? 'game' : 'games'}</span>
                  </span>
                  <span className="block text-xs text-mute font-normal">{s.sub}</span>
                </span>
                <ChevronDown size={24} aria-hidden className={`shrink-0 text-mute transition-transform ${isOpen ? 'rotate-180' : ''}`} />
              </button>
            </h2>
            <div id={panel} hidden={!isOpen} className={isOpen ? 'flex flex-col gap-3' : undefined}>
              {list.map((id) => card(id))}
            </div>
          </section>
        );
      })}
    </>
  );
}
